import type { MetricsResponse } from '@/common/api/models/metricsResponse';
import type { MetricsFiltersGated } from '@/common/api/models/metricsFiltersGated';
import type { Point } from '@/common/api/models/point';
import type { ReleaseLine } from '@/common/api/models/releaseLine';

import {
  asListFigure,
  asNumberFigure,
  asPointsFigure,
  findFigure,
} from '@/admin/dashboards/dashboardFigures';
import type { AiTutorViewModel } from '@/admin/dashboards/aiTutor/aiTutorViewModel';
import type {
  ChartPoint,
  NumberFigureView,
  ReasonRow,
  ReleaseMark,
} from '@/admin/dashboards/chartViewModel';

function partsRecord(
  parts: Point['extras']['parts'],
): Record<string, number> | null {
  if (!parts || typeof parts !== 'object') return null;
  const out: Record<string, number> = {};
  Object.entries(parts).forEach(([key, val]) => {
    if (typeof val === 'number') out[key] = val;
  });
  return Object.keys(out).length ? out : null;
}

export function mapChartPoint(point: Point): ChartPoint {
  return {
    x: point.x,
    value: point.value ?? null,
    n: point.extras?.n ?? null,
    tooFew: point.extras?.tooFew ?? null,
    p20: point.extras?.p20 ?? null,
    p80: point.extras?.p80 ?? null,
    parts: partsRecord(point.extras?.parts),
  };
}

export function mapChartPoints(points: Point[] | undefined): ChartPoint[] | null {
  if (!points?.length) return points ?? null;
  return points.map(mapChartPoint);
}

function mapNumberFigure(name: string, figures: MetricsResponse['figures']): NumberFigureView {
  const figure = asNumberFigure(findFigure(figures, name));
  const weekly = figure?.extras?.weekly;
  return {
    value: figure?.value ?? null,
    tooFew: !!figure?.extras?.tooFew,
    weekly: weekly?.length ? weekly.map(mapChartPoint) : weekly ?? null,
  };
}

function mapReleaseMark(release: ReleaseLine): ReleaseMark {
  return { at: release.at, prompt: release.prompt };
}

function mapReasonRows(figures: MetricsResponse['figures']): ReasonRow[] {
  const list = asListFigure(findFigure(figures, 'thumbs_down_reasons'));
  return (list?.value ?? []).map((item) => ({
    label: item.label,
    count: typeof item.value === 'number' ? item.value : 0,
  }));
}

export type GatedFilterInput = boolean | MetricsFiltersGated | null | undefined;

/** Pass-line threshold on the score chart: 100 when gated, null when ungated. */
export function scoreThresholdForGatedFilter(gated: GatedFilterInput): number | null {
  if (gated === true || gated === 'gated') return 100;
  return null;
}

/**
 * Maps a metrics response into the AI Tutor dashboard view model.
 * Generated API types stop here.
 */
export function mapAiTutorViewModel(
  metrics: MetricsResponse | null | undefined,
  gated: GatedFilterInput,
): AiTutorViewModel | null {
  if (!metrics?.figures) return null;

  const { figures, context } = metrics;
  const releaseLines = (context?.releaseLines ?? []).map(mapReleaseMark);

  return {
    scoreThreshold: scoreThresholdForGatedFilter(gated),
    releaseLines,
    attempts: mapNumberFigure('attempts_to_done', figures),
    scoreGain: mapNumberFigure('score_gain_per_attempt', figures),
    helpfulShare: mapNumberFigure('helpful_share', figures),
    pasteHeavyShare: mapNumberFigure('paste_heavy_share', figures),
    stuckStudents: mapNumberFigure('stuck_students', figures),
    voteRate: asNumberFigure(findFigure(figures, 'vote_rate'))?.value ?? null,
    voteRateTooFew: !!asNumberFigure(findFigure(figures, 'vote_rate'))?.extras?.tooFew,
    scoreByAttempt: mapChartPoints(asPointsFigure(findFigure(figures, 'score_by_attempt'))?.value),
    scoreBySessionWeek: mapChartPoints(
      asPointsFigure(findFigure(figures, 'score_by_session_week'))?.value,
    ),
    helpfulVotesWeek: mapChartPoints(
      asPointsFigure(findFigure(figures, 'helpful_votes:week'))?.value,
    ),
    voteRateWeek: mapChartPoints(asPointsFigure(findFigure(figures, 'vote_rate:week'))?.value),
    thumbsDownReasons: mapReasonRows(figures),
  };
}
